import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { industryAPI } from "@/services/industryService";
import { Industry } from "@/types/industry";
import { IndustriesTable } from "@/components/industries/IndustriesTable";
import { CreateIndustryModal } from "@/components/companies/CreateIndustryModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function IndustriesManagement() {
  const { company } = useAuth();
  const { toast } = useToast();
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState<Industry | null>(null);
  const [industryToDelete, setIndustryToDelete] = useState<Industry | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const companySlug = company?.slug || "";

  const loadIndustries = async () => {
    if (!companySlug) return;
    try {
      setLoading(true);
      const response = await industryAPI.getIndustries({
        slug: companySlug,
        search: searchQuery,
        is_active: 1, // Asegurar que solo traiga las activas
      });
      setIndustries(response.data);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar las industrias",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIndustries();
  }, [companySlug, searchQuery]);

  const handleCreate = () => {
    setEditingIndustry(null);
    setIsModalOpen(true);
  };

  const handleEdit = (industry: Industry) => {
    setEditingIndustry(industry);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (industry: Industry) => {
    setIndustryToDelete(industry);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!industryToDelete) return;
    try {
      await industryAPI.deleteIndustry(industryToDelete.id, companySlug);
      toast({
        title: "Éxito",
        description: "Industria eliminada correctamente",
      });
      loadIndustries();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar la industria",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setIndustryToDelete(null);
    }
  };

  const handleSave = async (data: { name: string; is_default: boolean }) => {
    try {
      if (editingIndustry) {
        await industryAPI.updateIndustry(editingIndustry.id, {
          ...data,
        });
        toast({
          title: "Éxito",
          description: "Industria actualizada correctamente",
        });
      } else {
        await industryAPI.createIndustry({
          ...data,
          slug: companySlug,
          is_active: true,
        });
        toast({
          title: "Éxito",
          description: "Industria creada correctamente",
        });
      }
      setIsModalOpen(false);
      loadIndustries();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar la industria",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Buscar industrias..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button onClick={handleCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Industria
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <IndustriesTable
          industries={industries}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />
      )}

      <CreateIndustryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        industryToEdit={editingIndustry}
        companySlug={companySlug}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              la industria "{industryToDelete?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
